import { useMemo } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { resources } from "@constants/hardcoded-strings";
import { GeneralValidationErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationAttribute } from "@idw-types/data/rp-service-configuration";
import { IDWSession } from "@idw-types/data/idw-session";
import { AxnCompany, AxnRelyingParty, SimpleAxnWorkflow } from "@idw-types/data/reference-data/axn";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { isAxnAdminUser } from "@utils/permissions-util";
import { ThemingSubTabContainer } from "@containers/common/theming-sub-tab-container/theming-sub-tab-container";
import { BasicInfoTabContainer } from "./basic-info-tab-container/basic-info-tab-container";
import { ServiceTypeTabContainer } from "./service-type-tab-container/service-type-tab-container";
import { IntegrationTypeTabContainer } from "./integration-type-tab-container/integration-type-tab-container";
import { AdvancedTabContainer } from "./advanced-tab-container/advanced-tab-container";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { RedirectUrlInformation } from "@idw-types/redirect-url-information";

type VerificationServiceDetailsGeneralTabProps = {
    isDisabled: boolean;
    session: IDWSession;
    validationErrors: GeneralValidationErrors;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    initialRpServiceConfigurationWorkflowID: string;
    simpleAxnWorkflows: Array<SimpleAxnWorkflow>;
    simpleAxnAdminWorkflows: Array<SimpleAxnWorkflow>;
    axnDocumentationLinks: Array<AxnDocumentationLink>;
    selectedLanguage: LocaleTypes;
    availableLanguages: Array<LocaleTypes>;
    isMultiLanguageEnabled: boolean;
    selectedAttributes: Array<FormattedRpServiceConfigurationAttribute>;
    showPinCodeSection: boolean;
    showIncodeFlowTabs: boolean;
    redirectUrlsInformation: RedirectUrlInformation;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
    handleGetRelyingPartyServicesRequest: (relyingPartyID: string) => void;
    handleGetWorkflowAppliedProfilesRequest: (workflowID: string) => void;
    handleCreateWorkflow: (workflowName: string) => void;
    updateProperties: (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => void;
};

export const VerificationServiceDetailsGeneralTab = (props: VerificationServiceDetailsGeneralTabProps) => {
    const {
        isDisabled,
        session,
        validationErrors,
        axnCompanies,
        axnRelyingParties,
        rpServiceConfigurationState,
        setRpServiceConfigurationState,
        initialRpServiceConfigurationWorkflowID,
        simpleAxnWorkflows,
        axnDocumentationLinks,
        selectedLanguage,
        availableLanguages,
        isMultiLanguageEnabled,
        selectedAttributes,
        showPinCodeSection,
        showIncodeFlowTabs,
        redirectUrlsInformation,
        handleGetRelyingPartyServicesRequest,
        handleGetWorkflowAppliedProfilesRequest,
        handleCreateWorkflow,
        simpleAxnAdminWorkflows,
        updateProperties
    } = props;

    const isAdminUser = useMemo(() => {
        return isAxnAdminUser(session.user);
    }, [session.user]);

    return (
        <ThemingSubTabContainer>
            <Tabs id="general" unmountOnExit={true}>
                <Tab eventKey={"general-basic-info"} title={validationErrors.basicInfo.tabTitle}>
                    <BasicInfoTabContainer
                        isDisabled={isDisabled}
                        session={session}
                        redirectUrlsInformation={redirectUrlsInformation}
                        validationErrors={validationErrors.basicInfo}
                        axnRelyingParties={axnRelyingParties}
                        axnCompanies={axnCompanies}
                        rpServiceConfigurationState={rpServiceConfigurationState}
                        setRpServiceConfigurationState={setRpServiceConfigurationState}
                        simpleAxnWorkflows={simpleAxnWorkflows}
                        simpleAxnAdminWorkflows={simpleAxnAdminWorkflows}   
                        initialRpServiceConfigurationWorkflowID={initialRpServiceConfigurationWorkflowID}
                        handleGetRelyingPartyServicesRequest={handleGetRelyingPartyServicesRequest}
                        handleGetWorkflowAppliedProfilesRequest={handleGetWorkflowAppliedProfilesRequest}
                        handleCreateWorkflow={handleCreateWorkflow}
                        updateProperties={updateProperties}
                    />
                </Tab>
                <Tab eventKey={"general-service-type"} title={resources.pages.verificationServiceDetails.tabs.general.subTabs.serviceType.tabName}>
                    <ServiceTypeTabContainer
                        isDisabled={isDisabled}
                        rpServiceConfigurationState={rpServiceConfigurationState}
                        setRpServiceConfigurationState={setRpServiceConfigurationState}
                    />
                </Tab>
                <Tab eventKey={"general-integration-type"} title={resources.pages.verificationServiceDetails.tabs.general.subTabs.integrationType.tabName}>
                    <IntegrationTypeTabContainer
                        isDisabled={isDisabled}
                        rpServiceConfigurationState={rpServiceConfigurationState}
                        setRpServiceConfigurationState={setRpServiceConfigurationState}
                    />
                </Tab>
                <Tab eventKey={"general-advanced"} title={validationErrors.advanced.tabTitle}>
                    <AdvancedTabContainer
                        axnDocumentationLinks={axnDocumentationLinks}
                        isDisabled={isDisabled}
                        isAdminUser={isAdminUser}
                        validationErrors={validationErrors.advanced}
                        rpServiceConfigurationState={rpServiceConfigurationState}
                        selectedAttributes={selectedAttributes}
                        selectedLanguage={selectedLanguage}
                        availableLanguages={availableLanguages}
                        isMultiLanguageEnabled={isMultiLanguageEnabled}
                        showPinCodeSection={showPinCodeSection}
                        showIncodeFlowTabs={showIncodeFlowTabs}
                        setRpServiceConfigurationState={setRpServiceConfigurationState}
                    />
                </Tab>
            </Tabs>
        </ThemingSubTabContainer>
    );
};